import React, { ReactElement, useState, useEffect, ReactNode, Ref, WeakValidationMap, RefAttributes } from 'react';
import { isUndefined } from '../utils/helpers';
import InteractiveList from '../InteractiveList';
import SelectionListItem, { SelectionListItemProps } from '../SelectionListItem';
import { forgeClassHelper } from '../utils/classes';
import FakeEvent from '../utils/fake-event';
import PropTypes, { Requireable, Validator } from 'prop-types';
import forwardRefToProps from '../utils/forwardRefToProps';

const classes = forgeClassHelper('selection-list');

export interface StructuredSelectionListOption {
  disabled?: boolean;
  text?: string;
  value: string;
  id?: string;
}

export type SelectionListOption = string | StructuredSelectionListOption;

export type SelectionListStructuredValue = { [key: string]: boolean };
export type SelectionListValue = SelectionListStructuredValue | '';

export type SelectionListOnChangeEvent = FakeEvent<SelectionListValue>;

export interface SelectionListProps {
  /** Specify the ID of the element that labels the SelectionList */
  'aria-labelledby'?: string;

  /** SelectionList items can be set through the options prop or through children, not both */
  children?: React.ReactNode;

  /** Adds a class to the root element of the component */
  className?: string;

  /**
   * Determines the initial set of selected items, but allows them to be updated internally (uncontrolled).
   * The object keys correspond to SelectedListItem.uniqueKey or option value.
   */
  defaultValue?: { [key: string]: boolean };

  /** Describes the selection options. Usually a question/statement that the options are responses to */
  description?: string;

  /** Impacts the styling of the checkboxes in addition to preventing selection when true */
  disabled?: boolean;

  /** Set to true to display dividers between each option */
  dividers?: boolean;

  /** Highlights the input when defined (specific string doesn't matter) */
  error?: string;

  /** Hides any required styling when true. Can be set manually or by the required field variation set for `Form`. */
  hideRequiredStyles?: boolean;

  /**
   * Adds an `id` attribute to the <fieldset> wrapping the list of options.
   * Is also used to generate unique ids and keys for each option's <input type="checkbox"/>,
   * unless option ids are provided in the `options` object
   */
  id?: string;

  /** `name` attribute of the root element */
  name?: string;

  /**
   * Called when any of the items lose focus.
   * Gets a simulated event object. The value (map of keys to true/false) is in `event.target.value`.
   */
  onBlur?: (event: SelectionListOnChangeEvent) => void;

  /**
   * Called when the value changes.
   * Gets a simulated event object. The value (map of keys to true/false) is in `event.target.value`.
   */
  onChange?: (event: SelectionListOnChangeEvent) => void;

  /**
   * A callback handling item click events. You would generally use `onChange` instead.
   * Arguments passed to the callback are the synthetic event and the unique key
   * of the associated list item.
   */
  onClickItem?: (
    event: React.MouseEvent<HTMLUListElement, MouseEvent> | React.KeyboardEvent<HTMLUListElement>,
    key: string | null
  ) => void;

  /**
   * Function that takes a JS event as an argument. Called when the SelectionList receives focus.
   */
  onFocus?: (event: React.FocusEvent<HTMLUListElement, Element>) => void;

  /**
   * If an option is supplied as a string; it's both the value and the text of the option.
   * If an option is supplied as an object:
   *  - The `value` property will be used to provide a unique React `key` for the option,
   *    and will set the `value` attribute on the option's <input type="checkbox"/>.
   *  - The `id` property will be used to set the HTML id attribute on the option's <input type="checkbox">.
   *  - If `id` is not provided, an id attribute will be generated using either the SelectionList's `id` prop
   *    or the option's `value` property.
   */
  options?: SelectionListOption[];

  /** Adds default padding that can be removed when the parent already provides a gutter. */
  padded?: boolean;

  /** Style the input as required when true. Only used for styling purposes. All validation is handled by parents. */
  required?: boolean;

  /**
   * Determines the set of selected items, and prevents them from getting updated internally ([controlled](https://reactjs.org/docs/forms.html#controlled-components)).
   * The object keys correspond to SelectedListItem.uniqueKey or option value.
   */
  value?: SelectionListValue;
}

export interface SelectionListComponentProps extends SelectionListProps {
  /** Ref forwarded to SelectionList's input element */
  forwardedRef?: React.Ref<HTMLInputElement>;
}

function getValueFromItems({
  children,
  options,
}: Pick<SelectionListProps, 'children' | 'options'>): SelectionListStructuredValue {
  const value: SelectionListStructuredValue = {};
  if (children) {
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child)) {
        const elementChild: React.ReactElement = child;
        if (child.props.children) {
          const childType = elementChild.type as string | { name: string };
          if (typeof childType !== 'string' && childType.name === 'SelectionListItem') {
            value[child.props.uniqueKey] = child.props.selected || false;
          }
        }
      }
    });
  } else {
    // Options can't be defined as selected, but we still want to record them all
    options?.forEach((option) => {
      if (typeof option === 'object') {
        value[option.value] = false;
      } else {
        value[option] = false;
      }
    });
  }
  return value;
}

function reconcileValueAndDefaultValue({
  value,
  defaultValue,
}: Pick<SelectionListProps, 'value' | 'defaultValue'>): SelectionListStructuredValue {
  if (value === '') {
    return {};
  } else if (!isUndefined(value)) {
    return { ...value } || {};
  } else if (!isUndefined(defaultValue)) {
    return { ...defaultValue } || {};
  }
  return {};
}

/**
 * SelectionList
 *
 * A component to render an unordered list of selectable items.
 *
 */
function SelectionListComponent({
  'aria-labelledby': ariaLabelledBy,
  disabled = false,
  dividers = false,
  padded = true,
  hideRequiredStyles = false,
  required = false,
  className,
  error,
  defaultValue: upstreamDefaultValue,
  value: upstreamValue,
  onChange,
  onBlur,
  description,
  id,
  forwardedRef,
  children,
  options,
  onClickItem,
  ...listProps
}: SelectionListComponentProps): ReactElement {
  const getInitialValueFromProps = (): SelectionListStructuredValue => {
    const itemValue = getValueFromItems({ children, options });
    const valueFromProps = reconcileValueAndDefaultValue({ value: upstreamValue, defaultValue: upstreamDefaultValue });
    for (const itemKey in itemValue) {
      // Coerce to a boolean so that the child is a controlled component
      // See: https://reactjs.org/docs/forms.html#controlled-components
      itemValue[itemKey] = valueFromProps[itemKey] || false;
    }
    return itemValue;
  };

  const [value, setValue] = useState(getInitialValueFromProps);

  useEffect(() => {
    if (upstreamValue) setValue({ ...upstreamValue });
  }, [upstreamValue]);

  /**
   * Toggle whether or not a given item is selected.
   */
  function toggleItem(
    event: React.MouseEvent<HTMLUListElement, MouseEvent> | React.KeyboardEvent<HTMLUListElement>,
    key: string | null
  ): void {
    if (onClickItem) {
      onClickItem(event, key);
    }
    if (isUndefined(upstreamValue)) {
      setValue((prev) => {
        const newVal = toggleValue(prev, key);
        handleChange(newVal);
        return newVal;
      });
    } else {
      handleChange(toggleValue(value, key));
    }
  }

  function toggleValue(previousValue: SelectionListStructuredValue, key: string | null): SelectionListStructuredValue {
    if (typeof key === 'string') {
      return {
        ...previousValue,
        [key]: !previousValue[key],
      };
    } else {
      return previousValue;
    }
  }

  function handleChange(newValue?: SelectionListValue): void {
    const val = isUndefined(newValue) ? value : newValue;
    if (onChange) {
      onChange(new FakeEvent({ value: val, id: id }) as SelectionListOnChangeEvent);
    }
  }

  function handleBlur(): void {
    if (onBlur) onBlur(new FakeEvent({ value, id: id }) as SelectionListOnChangeEvent);
  }

  /**
   * Render the children of this list.
   */
  function renderChildren(forwardedRef?: Ref<HTMLInputElement>): ReactNode {
    if (children) {
      return React.Children.map(children, (child, i) => {
        if (React.isValidElement<SelectionListItemProps>(child)) {
          return React.cloneElement(child, {
            selected: value[child.props.uniqueKey] || false,
            disabled: child.props.disabled || disabled,
            error: error,
            forwardedRef: i === 0 ? forwardedRef : null,
          });
        } else {
          return child;
        }
      });
    } else if (options) {
      return options.map((option, i) => {
        if (typeof option === 'string') {
          option = {
            value: option,
            id: id ? id + '-' + option : option,
          };
        }
        return (
          <SelectionListItem
            uniqueKey={option.id || (id ? id + '-' + option.value : option.value)}
            key={option.value}
            disabled={disabled || option.disabled}
            selected={value[option.value] || false}
            error={error}
            forwardedRef={i === 0 ? forwardedRef : null}
          >
            {option.text ? option.text : option.value}
          </SelectionListItem>
        );
      });
    }
    return null;
  }

  return (
    <fieldset
      {...classes({
        states: {
          disabled,
          error: error ? !disabled : false,
          required: required && !hideRequiredStyles && !disabled,
        },
        modifiers: {
          padded,
          'required-styles-hidden': hideRequiredStyles,
        },
        extra: className,
      })}
      aria-labelledby={ariaLabelledBy}
      {...(required && { 'aria-required': 'true' })}
      {...(disabled && { 'aria-disabled': 'true' })}
      id={id}
    >
      {description && <legend {...classes('description')}>{description}</legend>}

      <InteractiveList
        {...classes('list')}
        disabled={disabled}
        dividers={dividers}
        onClickItem={toggleItem}
        onBlur={handleBlur}
        padded={false}
        {...listProps}
      >
        {renderChildren(forwardedRef)}
      </InteractiveList>
    </fieldset>
  );
}

const selectionListPropTypes: WeakValidationMap<
  SelectionListComponentProps & {
    '...rest': unknown;
  }
> = {
  //: WeakValidationMap<SelectionListComponentProps & { '...rest': unknown }> = {
  /** Specify the ID of the element that labels the SelectionList */
  'aria-labelledby': PropTypes.string,
  /** SelectionList items can be set through the options prop or through children, not both */
  children: function (props) {
    if ((props.options && props.children) || !(props.options || props.children)) {
      return new Error('Select options must be set through the options prop or through children, but not both.');
    }
    return null;
  },
  /** Adds a class to the root element of the component */
  className: PropTypes.string,
  /**
   * Determines the initial set of selected items, but allows them to be updated internally ([uncontrolled](https://reactjs.org/docs/uncontrolled-components.html)).
   * The object keys correspond to SelectedListItem.uniqueKey or option value.
   */
  defaultValue: PropTypes.objectOf(PropTypes.bool.isRequired),
  /** Describes the selection options. Usually a question/statement that the options are responses to */
  description: PropTypes.string,
  /** Impacts the styling of the checkboxes in addition to preventing selection */
  disabled: PropTypes.bool,
  /** Set to true to display dividers between each item */
  dividers: PropTypes.bool,
  /** Highlights the input when defined (specific string doesn't matter) */
  error: PropTypes.string,
  /** Hides any required styling. Can be set manually or by the required field variation set for `Form`. */
  hideRequiredStyles: PropTypes.bool,
  /**
   * Adds an `id` attribute to the `<fieldset>` wrapping the list of options.
   * Is also used to generate unique ids and keys for each option's `<input type="checkbox"/>`,
   * unless option ids are provided in the `options` object
   */
  id: PropTypes.string,
  /** `name` attribute of the root element */
  name: PropTypes.string,
  /**
   * Function that takes a JS event as an argument. Called when the SelectionList loses focus
   */
  onBlur: PropTypes.func,
  /**
   * Function that takes a fake event as an argument. This event contains a target object with value and id properties. Called when the SelectionList value changes
   */
  onChange: PropTypes.func,
  /**
   * A callback handling item click events. You would generally use `onChange` instead.
   * Arguments passed to the callback are the synthetic event and the unique key of the associated list item.
   */
  onClickItem: PropTypes.func,
  /**
   * Function that takes a JS event as an argument. Called when the SelectionList receives focus
   */
  onFocus: PropTypes.func,
  /**
   * If an option is supplied as a string, it's both the value and the text of the option.
   *
   * If an option is supplied as an object:
   *  - The `value` property will be used to provide a unique React `key` for the option,
   *    and will set the `value` attribute on the option's `<input type="checkbox"/>`.
   *  - The `id` property will be used to set the HTML id attribute on the option's `<input type="checkbox">`.
   *  - If `id` is not provided, an id attribute will be generated using either the SelectionList's `id` prop
   *    or the option's `value` property.
   */
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string.isRequired,
      PropTypes.shape({
        disabled: PropTypes.bool,
        text: PropTypes.string,
        value: PropTypes.string.isRequired,
        id: PropTypes.string,
      }).isRequired as Validator<StructuredSelectionListOption>,
    ]).isRequired
  ),
  /** Adds default padding that can be removed when the parent already provides a gutter */
  padded: PropTypes.bool,
  /** Ref forwarded to the input element */
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    /** Note: { current: PropTypes.instanceOf(HTMLInputElement) } would be more
     *  correct, but causes a StackOverflow when building gatsby.
     */
    PropTypes.shape({ current: PropTypes.any }),
  ]) as Validator<React.Ref<HTMLInputElement>>,
  /** Only used for styling purposes. All validation is handled by parents. */
  required: PropTypes.bool,
  /**
   * Determines the set of selected items, and prevents them from getting updated internally ([controlled](https://reactjs.org/docs/forms.html#controlled-components)).
   * The object keys correspond to SelectedListItem.uniqueKey or option value.
   */
  value: PropTypes.oneOfType([
    PropTypes.objectOf(PropTypes.bool.isRequired).isRequired,
    (PropTypes.oneOf(['']) as Requireable<''>).isRequired,
  ]),
  /**
   * Passthrough props to the `InteractiveList` sub-component
   */
  '...rest': PropTypes.any,
};
SelectionListComponent.propTypes = selectionListPropTypes;
SelectionListComponent.displayName = 'SelectionList';

export type SelectionListDefaultExport = (props: SelectionListProps & RefAttributes<HTMLInputElement>) => ReactElement;

export default forwardRefToProps(SelectionListComponent) as SelectionListDefaultExport;
